// TrajectoryGraphPopup.jsx
import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import './TrajectoryGraphPopup.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

const TrajectoryGraphPopup = ({ data, onClose, user, selectedUser, highlightMeeting }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [graphData, setGraphData] = useState([]);
  const [stats, setStats] = useState({
    totalMeetings: 0,
    avgOverall: 0,
    avgVoice: 0,
    avgCamera: 0,
    avgWearable: 0,
    bestOverall: 0,
    trend: 'stable'
  });

  useEffect(() => {
    if (data && data.meetings) {
      // Extract meetings from the data
      let meetings = data.meetings;
      
      // If meetings is an array of objects with meeting_info and result
      // (like from the API response)
      if (meetings.length > 0 && meetings[0].result) {
        meetings = meetings.map(m => ({
          ...m,
          ...m.meeting_info,
          ...m.result,
          meeting_id: m.meeting_id || m.meetingId,
        }));
      }

      const transformed = meetings.map((meeting, index) => {
        const date = meeting.start_time ? new Date(meeting.start_time) : new Date();
        
        // Get scores with proper fallbacks
        const overall = meeting.overall_score != null ? Math.round(meeting.overall_score * 100) : 0;
        const voice = meeting.voice_score != null ? Math.round(meeting.voice_score * 100) : 0;
        const camera = meeting.camera_score != null ? Math.round(meeting.camera_score * 100) : 0;
        const wearable = meeting.wearable_score != null ? Math.round(meeting.wearable_score * 100) : 0;
        const synchrony = meeting.biometric_synchrony_score != null ? Math.round(meeting.biometric_synchrony_score * 100) : 0;
        const resonance = meeting.meeting_resonance_score != null ? Math.round(meeting.meeting_resonance_score) : 0;
        
        return {
          meetingNumber: index + 1,
          date: date.toLocaleDateString(),
          time: date.toLocaleTimeString(),
          overall,
          voice,
          camera,
          wearable,
          synchrony,
          resonance,
          sharedState: meeting.shared_state || 'unknown',
          dataQuality: meeting.data_quality || 'poor',
          meetingId: meeting.meeting_id || meeting.meetingId || index,
          // Store raw scores for tooltips
          rawOverall: meeting.overall_score,
          rawVoice: meeting.voice_score,
          rawCamera: meeting.camera_score,
          rawWearable: meeting.wearable_score,
        };
      });

      // Sort by meeting number
      transformed.sort((a, b) => a.meetingNumber - b.meetingNumber);
      
      setGraphData(transformed);

      // Calculate stats
      const total = transformed.length;
      const avgOverall = transformed.reduce((sum, d) => sum + d.overall, 0) / total;
      const avgVoice = transformed.reduce((sum, d) => sum + d.voice, 0) / total;
      const avgCamera = transformed.reduce((sum, d) => sum + d.camera, 0) / total;
      const avgWearable = transformed.reduce((sum, d) => sum + d.wearable, 0) / total;
      const bestOverall = Math.max(...transformed.map(d => d.overall));
      
      // Determine trend
      let trend = 'stable';
      if (transformed.length >= 3) {
        const firstHalf = transformed.slice(0, Math.floor(transformed.length / 2));
        const secondHalf = transformed.slice(Math.floor(transformed.length / 2));
        const avgFirst = firstHalf.reduce((sum, d) => sum + d.overall, 0) / firstHalf.length;
        const avgSecond = secondHalf.reduce((sum, d) => sum + d.overall, 0) / secondHalf.length;
        const diff = avgSecond - avgFirst;
        if (diff > 5) trend = 'improving';
        else if (diff < -5) trend = 'draining';
      }

      setStats({
        totalMeetings: total,
        avgOverall: Math.round(avgOverall),
        avgVoice: Math.round(avgVoice),
        avgCamera: Math.round(avgCamera),
        avgWearable: Math.round(avgWearable),
        bestOverall: Math.round(bestOverall),
        trend
      });
    }
  }, [data]);

  const getStateColor = (state) => {
    const colors = {
      calm_aligned: '#00b894',
      engaged_aligned: '#6c5ce7',
      shared_stress: '#fdcb6e',
      mismatch: '#e17055',
      insufficient_data: '#b2bec3',
      unknown: '#b2bec3',
    };
    return colors[state] || '#b2bec3';
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#2d3436',
          font: { size: 12, weight: '500' },
          boxWidth: 12,
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.85)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y}%`;
          },
          afterBody: function(tooltipItems) {
            const dataIndex = tooltipItems[0].dataIndex;
            const item = graphData[dataIndex];
            if (!item) return [];
            return [
              `Date: ${item.date}`,
              `Time: ${item.time}`,
              `State: ${item.sharedState.replace('_', ' ')}`,
              `Quality: ${item.dataQuality}`
            ];
          }
        }
      },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: { color: '#636e72', callback: (value) => value + '%' },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      x: {
        ticks: { color: '#636e72' },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
    },
  };

  // Prepare chart data
  const labels = graphData.map(d => `#${d.meetingNumber}`);

  const overviewChartData = {
    labels,
    datasets: [
      {
        label: 'Overall',
        data: graphData.map(d => d.overall),
        borderColor: '#e17055',
        backgroundColor: 'rgba(225, 112, 85, 0.15)',
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointBackgroundColor: '#e17055',
        borderWidth: 3,
      },
      {
        label: 'Resonance',
        data: graphData.map(d => d.resonance),
        borderColor: '#00b894',
        backgroundColor: 'rgba(0, 184, 148, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#00b894',
        borderDash: [5, 5],
      },
      {
        label: 'Synchrony',
        data: graphData.map(d => d.synchrony),
        borderColor: '#6c5ce7',
        backgroundColor: 'rgba(108, 92, 231, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#6c5ce7',
        borderDash: [3, 3],
      },
    ],
  };

  const modalitiesChartData = {
    labels,
    datasets: [
      {
        label: 'Voice',
        data: graphData.map(d => d.voice),
        borderColor: '#00b894',
        backgroundColor: 'rgba(0, 184, 148, 0.15)',
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: '#00b894',
        borderWidth: 2,
      },
      {
        label: 'Camera',
        data: graphData.map(d => d.camera),
        borderColor: '#fdcb6e',
        backgroundColor: 'rgba(253, 203, 110, 0.15)',
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: '#fdcb6e',
        borderWidth: 2,
      },
      {
        label: 'Wearable',
        data: graphData.map(d => d.wearable),
        borderColor: '#0984e3',
        backgroundColor: 'rgba(9, 132, 227, 0.15)',
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: '#0984e3',
        borderDash: [5, 5],
        borderWidth: 2,
      },
    ],
  };

  const getStateDistribution = () => {
    const states = {};
    graphData.forEach(d => {
      const key = d.sharedState || 'unknown';
      states[key] = (states[key] || 0) + 1;
    });
    return {
      labels: Object.keys(states).map(s => s.replace(/_/g, ' ').toUpperCase()),
      datasets: [
        {
          data: Object.values(states),
          backgroundColor: Object.keys(states).map(s => getStateColor(s)),
          borderColor: '#fff',
          borderWidth: 2,
        },
      ],
    };
  };

  return (
    <div className="trajectory-graph-overlay" onClick={onClose}>
      <div className="trajectory-graph-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <h2>
            📊 Meeting Trajectory
            <span className="popup-subtitle">
              {user?.first_name} & {selectedUser?.first_name}
            </span>
          </h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="popup-tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'modalities' ? 'active' : ''}`}
            onClick={() => setActiveTab('modalities')}
          >
            Modalities
          </button>
          <button 
            className={`tab-btn ${activeTab === 'insights' ? 'active' : ''}`}
            onClick={() => setActiveTab('insights')}
          >
            Insights
          </button>
        </div>

        <div className="popup-content">
          {activeTab === 'overview' && (
            <div className="graph-section">
              <h3>Overall Scores Over Time</h3>
              <div style={{ height: 300 }}>
                <Line data={overviewChartData} options={chartOptions} />
              </div>

              <div className="stats-summary">
                <div className="stat-item">
                  <span className="stat-label">Meetings</span>
                  <span className="stat-value">{stats.totalMeetings}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Avg Overall</span>
                  <span className="stat-value">{stats.avgOverall}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Trend</span>
                  <span className="stat-value" style={{ 
                    color: stats.trend === 'improving' ? '#00b894' : 
                           stats.trend === 'draining' ? '#e17055' : '#fdcb6e'
                  }}>
                    {stats.trend === 'improving' ? '📈 Improving' :
                     stats.trend === 'draining' ? '📉 Draining' :
                     '➡️ Stable'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Best Score</span>
                  <span className="stat-value">{stats.bestOverall}%</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'modalities' && (
            <div className="graph-section">
              <h3>Modality Scores Over Time</h3>
              <div style={{ height: 300 }}>
                <Line data={modalitiesChartData} options={chartOptions} />
              </div>

              <div className="modality-stats">
                <div className="modality-item">
                  <span className="modality-dot voice-dot"></span>
                  <span className="modality-label">Voice</span>
                  <span className="modality-value">{stats.avgVoice}%</span>
                  <span className="modality-trend">
                    {graphData.length > 0 && graphData[graphData.length - 1].voice > graphData[0].voice ? '↑' : 
                     graphData.length > 0 && graphData[graphData.length - 1].voice < graphData[0].voice ? '↓' : '→'}
                  </span>
                </div>
                <div className="modality-item">
                  <span className="modality-dot camera-dot"></span>
                  <span className="modality-label">Camera</span>
                  <span className="modality-value">{stats.avgCamera}%</span>
                  <span className="modality-trend">
                    {graphData.length > 0 && graphData[graphData.length - 1].camera > graphData[0].camera ? '↑' : 
                     graphData.length > 0 && graphData[graphData.length - 1].camera < graphData[0].camera ? '↓' : '→'}
                  </span>
                </div>
                <div className="modality-item">
                  <span className="modality-dot wearable-dot"></span>
                  <span className="modality-label">Wearable</span>
                  <span className="modality-value">{stats.avgWearable}%</span>
                  <span className="modality-trend">
                    {graphData.length > 0 && graphData[graphData.length - 1].wearable > graphData[0].wearable ? '↑' : 
                     graphData.length > 0 && graphData[graphData.length - 1].wearable < graphData[0].wearable ? '↓' : '→'}
                  </span>
                </div>
              </div>

              <div className="shared-states">
                <h4>Shared States Distribution</h4>
                <div style={{ height: 150, maxWidth: 300, margin: '0 auto' }}>
                  <Doughnut data={getStateDistribution()} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: {
                          boxWidth: 12,
                          padding: 10,
                          font: { size: 11 },
                        }
                      }
                    }
                  }} />
                </div>
              </div>

              <div className="meeting-details-table">
                <h4>Meeting Details</h4>
                <div className="table-scroll">
                  <table className="meeting-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Overall</th>
                        <th>Voice</th>
                        <th>Camera</th>
                        <th>Wearable</th>
                        <th>State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {graphData.map((d, i) => (
                        <tr key={i} className={d.meetingId === highlightMeeting?.meeting_id ? 'highlighted' : ''}>
                          <td>{d.meetingNumber}</td>
                          <td>{d.date}</td>
                          <td className="score-high">{d.overall}%</td>
                          <td className="score-good">{d.voice}%</td>
                          <td className="score-good">{d.camera}%</td>
                          <td className={d.wearable > 0 ? 'score-good' : 'score-low'}>
                            {d.wearable}%
                          </td>
                          <td>
                            <span className="state-badge" style={{ backgroundColor: getStateColor(d.sharedState) }}>
                              {d.sharedState.replace('_', ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="insights-section">
              <h3>AI Insights</h3>
              
              {/* Summary Stats */}
              <div className="insight-summary-cards">
                <div className="summary-card">
                  <span className="summary-label">Total Meetings</span>
                  <span className="summary-value">{stats.totalMeetings}</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Avg Overall</span>
                  <span className="summary-value">{stats.avgOverall}%</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Avg Voice</span>
                  <span className="summary-value">{stats.avgVoice}%</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Avg Camera</span>
                  <span className="summary-value">{stats.avgCamera}%</span>
                </div>
              </div>

              {data?.strength_patterns && data.strength_patterns.length > 0 && (
                <div className="insight-group strength">
                  <h4>💪 Strengths</h4>
                  <ul>
                    {data.strength_patterns.map((insight, i) => (
                      <li key={i}>{insight}</li>
                    ))}
                  </ul>
                </div>
              )}

              {data?.friction_patterns && data.friction_patterns.length > 0 && (
                <div className="insight-group friction">
                  <h4>⚠️ Areas to Work On</h4>
                  <ul>
                    {data.friction_patterns.map((insight, i) => (
                      <li key={i}>{insight}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Auto-generated insights from data */}
              <div className="insight-group auto">
                <h4>📊 Data Insights</h4>
                <ul>
                  {stats.avgOverall >= 90 && (
                    <li>✅ Overall compatibility is excellent across all meetings ({stats.avgOverall}% average).</li>
                  )}
                  {stats.avgVoice >= 90 && (
                    <li>🎤 Voice synchrony is consistently strong ({stats.avgVoice}% average).</li>
                  )}
                  {stats.avgCamera >= 90 && (
                    <li>📷 Visual engagement is consistently strong ({stats.avgCamera}% average).</li>
                  )}
                  {stats.avgWearable === 0 && (
                    <li>⚠️ No wearable data available. Consider enabling wearable tracking for more comprehensive insights.</li>
                  )}
                  {stats.avgWearable > 0 && stats.avgWearable < 50 && (
                    <li>⌚ Wearable synchrony is low ({stats.avgWearable}% average). This may indicate physical misalignment.</li>
                  )}
                  {stats.trend === 'improving' && (
                    <li>📈 Overall scores are trending upward. Keep up the good work!</li>
                  )}
                  {stats.trend === 'draining' && (
                    <li>📉 Overall scores are trending downward. Consider addressing potential issues.</li>
                  )}
                  {graphData.length >= 2 && (
                    <li>📊 {graphData.length} meetings analyzed. Latest overall score: {graphData[graphData.length-1]?.overall || 0}%</li>
                  )}
                </ul>
              </div>

              {data?.notes && data.notes.length > 0 && (
                <div className="insight-group notes">
                  <h4>📝 Notes</h4>
                  <ul>
                    {data.notes.map((note, i) => (
                      <li key={i}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="trajectory-summary">
                <h4>📈 Trajectory Summary</h4>
                <div className="trajectory-grid">
                  <div className="trajectory-item">
                    <span className="trajectory-label">Regulation</span>
                    <span className={`trajectory-value ${data?.trajectory?.regulation || 'insufficient_data'}`}>
                      {data?.trajectory?.regulation || 'insufficient_data'}
                    </span>
                  </div>
                  <div className="trajectory-item">
                    <span className="trajectory-label">Repair</span>
                    <span className={`trajectory-value ${data?.trajectory?.repair || 'insufficient_data'}`}>
                      {data?.trajectory?.repair || 'insufficient_data'}
                    </span>
                  </div>
                  <div className="trajectory-item">
                    <span className="trajectory-label">Integration</span>
                    <span className={`trajectory-value ${data?.trajectory?.integration || 'insufficient_data'}`}>
                      {data?.trajectory?.integration || 'insufficient_data'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="popup-footer">
          <button className="close-popup-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrajectoryGraphPopup;